import '@alphadoc/alphadoc-gantt/styles';
import '@/assets/styles/index.scss';
import { createApp } from 'vue';
import App from '@/App.vue';
import * as Sentry from '@sentry/vue';
import Exception from '@/modules/core/utils/exception.ts';
import hotkeys from 'hotkeys-js';
import i18n from '@/i18n.ts';
import pinia from '@/pinia.ts';
import router from '@/router.ts';
import tanstackQueryOptions, { queryClient } from '@/tanstack-query.ts';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { Settings } from 'luxon';
import vPrefix from '@/modules/common/directives/prefix.ts';
import vPostfix from '@/modules/common/directives/postfix.ts';
import vRotate from '@/modules/common/directives/rotate.ts';
import vFocus from '@/modules/common/directives/focus.ts';
import { setupAuthSessionSync } from '@/modules/core/composables/auth-session-sync.ts';
import { setupDocumentUpdateSync } from '@/modules/shared/composables/document-update-sync.ts';
import { usePreviewStore } from '@/modules/core/stores/preview.store.ts';
import { useDocumentEditStore } from '@/modules/document/document-edit/stores/document-edit.store.ts';
import { getLastSelectedId } from '@/modules/viewer/composables/use-viewer-selection.ts';
import { setupTextSelectionGuard } from '@/modules/ui/utils/text-selection-guard.ts';

const app = createApp(App);

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({ app, dsn: import.meta.env.VITE_SENTRY_DSN });
}

if (import.meta.env.PROD) {
  Exception.init(app);
}

Settings.defaultLocale = i18n.global.locale.value;
hotkeys.filter = () => true;

app.config.globalProperties.$maybeT = import.meta.env.PROD ? i18n.global.t : i18n.global.maybeT;
app.config.globalProperties.$o = i18n.global.o;

app
  .use(i18n)
  .use(pinia)
  .use(router)
  .use(VueQueryPlugin, tanstackQueryOptions)
  .directive('prefix', vPrefix)
  .directive('postfix', vPostfix)
  .directive('rotate', vRotate)
  .directive('focus', vFocus);

setupAuthSessionSync();
setupTextSelectionGuard();

const previewStore = usePreviewStore();
const documentEditStore = useDocumentEditStore();

setupDocumentUpdateSync(queryClient, {
  getActiveDocumentContext(documentId) {
    if (previewStore.entityIdPreview !== documentId || documentEditStore.isEditingDocument(documentId)) {
      return null;
    }

    const scope = router.currentRoute.value.name?.toString() ?? null;

    return {
      scope,
      rowId: scope ? getLastSelectedId(scope) : null,
      refresh: () => previewStore.refreshPreviewAction(),
    };
  },
});

app.mount('#app');
